import { type Completion, snippetCompletion } from "@codemirror/autocomplete";
import {
  foldInside,
  foldNodeProp,
  HighlightStyle,
  indentNodeProp,
  indentRange,
  LanguageSupport,
  LRLanguage,
  type TreeIndentContext,
} from "@codemirror/language";
import type { StateCommand } from "@codemirror/state";
import { EditorView } from "@codemirror/view";
import { styleTags, tags as t } from "@lezer/highlight";
import { completionItems, rhaiWordLists } from "@/components/rhai-language";
import { knownFunctionMarks } from "./rhai/knownFunctionMarks";
import { parser } from "./rhai/rhai.grammar";

// The Studio's Rhai language (TTTL-225): a Lezer parser (rhai/rhai.grammar)
// whose tree drives highlighting, folding, indentation and the scope
// linter alike. The word lists (components/rhai-language) still feed the
// completions; the mstream helpers keep their bold rendering through a
// decoration over the tree's call nodes.

const KEYWORDS = new Set(rhaiWordLists.keywords);
const CONSTANTS = new Set(rhaiWordLists.builtinConstants);
const TYPES = new Set(rhaiWordLists.typeKeywords);
const BUILTINS = new Set(rhaiWordLists.builtinFunctions);
const MSTREAM = new Set(rhaiWordLists.mstreamFunctions);

// The bracketed nodes that each add one indentation level.
const NESTING = new Set([
  "Block",
  "ObjectLiteral",
  "ArrayLiteral",
  "ParamList",
  "ArgList",
  "ParenthesizedExpression",
]);

// Indentation by structural depth — one unit per enclosing bracket node,
// the closing bracket dedenting its own line. Structural rather than
// relative to the opening line's current indent (delimitedIndent), so the
// Format action can reindent a flat script in one pass.
function nestingIndent(closing: string) {
  return (cx: TreeIndentContext): number => {
    let depth = 0;
    for (let n = cx.node.parent; n; n = n.parent)
      if (NESTING.has(n.name)) depth++;
    const closes = cx.textAfter.trimStart().startsWith(closing);
    return (depth + (closes ? 0 : 1)) * cx.unit;
  };
}

/** The configured parser — node props for the editor, shared with the
 *  scope pass (rhai/rhaiScope) and the tests. */
export const rhaiParser = parser.configure({
  props: [
    styleTags({
      "fn let const if else switch while loop do until for in try catch return throw break continue private":
        t.keyword,
      this: t.self,
      BooleanLiteral: t.bool,
      FunctionName: t.function(t.definition(t.variableName)),
      "CallExpression/VariableName": t.function(t.variableName),
      VariableDefinition: t.definition(t.variableName),
      VariableName: t.variableName,
      PropertyName: t.propertyName,
      "Property/PropertyName": t.definition(t.propertyName),
      Path: t.namespace,
      String: t.string,
      Char: t.character,
      TemplateString: t.special(t.string),
      "Integer Float": t.number,
      LineComment: t.lineComment,
      BlockComment: t.blockComment,
      DocComment: t.docComment,
      "ArithOp BitOp CompareOp UpdateOp": t.operator,
      "LogicOp =>": t.operatorKeyword,
      "( )": t.paren,
      "[ ]": t.squareBracket,
      "{ } #{": t.brace,
      ". ?. , ; : ::": t.punctuation,
    }),
    indentNodeProp.add({
      "Block ObjectLiteral": nestingIndent("}"),
      ArrayLiteral: nestingIndent("]"),
      "ParamList ArgList ParenthesizedExpression": nestingIndent(")"),
      // Continuation lines of a comment or template keep their own indent.
      "BlockComment TemplateString": () => null,
    }),
    foldNodeProp.add({
      "Block ObjectLiteral ArrayLiteral": foldInside,
      BlockComment: (node) => ({ from: node.from + 2, to: node.to - 2 }),
    }),
  ],
});

// The 3t-bridge-theme Rhai palette, token for token (Monaco parity).
export const rhaiHighlightStyle = HighlightStyle.define([
  { tag: t.keyword, color: "#AF00DB" },
  { tag: [t.bool, t.self], color: "#0000FF", fontWeight: "bold" },
  { tag: [t.typeName, t.namespace], color: "#267F99" },
  {
    tag: [t.function(t.variableName), t.function(t.definition(t.variableName))],
    color: "#795E26",
  },
  { tag: t.macroName, color: "#795E26", fontWeight: "bold" },
  {
    tag: [
      t.variableName,
      t.definition(t.variableName),
      t.propertyName,
      t.definition(t.propertyName),
    ],
    color: "#001080",
  },
  { tag: [t.string, t.special(t.string), t.character], color: "#A31515" },
  { tag: t.number, color: "#098658" },
  { tag: [t.comment, t.lineComment, t.blockComment], color: "#008000" },
  { tag: t.docComment, color: "#008000", fontStyle: "italic" },
  { tag: t.operator, color: "#000000" },
  { tag: t.operatorKeyword, color: "#AF00DB" },
]);

// The shared completion items (rhai-language): snippet insertions for the
// mstream helpers and the transform scaffold — the ${n:placeholder} snippet
// syntax is what CodeMirror's snippet parser speaks — plus plain word
// completions for keywords, types and builtins.
const SNIPPET_LABELS = new Set(completionItems.map((item) => item.label));

const rhaiCompletions: Completion[] = [
  ...completionItems.map((item) =>
    snippetCompletion(item.insertText, {
      label: item.label,
      detail: item.detail,
      info: item.documentation,
      type: "function",
    }),
  ),
  ...[...KEYWORDS].map(
    (word): Completion => ({ label: word, type: "keyword" }),
  ),
  ...[...TYPES].map((word): Completion => ({ label: word, type: "type" })),
  ...[...CONSTANTS].map(
    (word): Completion => ({ label: word, type: "constant" }),
  ),
  ...[...BUILTINS]
    .filter((word) => !SNIPPET_LABELS.has(word))
    .map((word): Completion => ({ label: word, type: "function" })),
];

const rhaiLRLanguage = LRLanguage.define({
  name: "rhai",
  parser: rhaiParser,
  languageData: {
    commentTokens: { line: "//", block: { open: "/*", close: "*/" } },
    closeBrackets: { brackets: ["(", "[", "{", "'", '"', "`"] },
    autocomplete: rhaiCompletions,
  },
});

/** The editor extension: the language, plus the bold rendering of calls
 *  to the mstream helpers (the tokenizer's old `macroName` styling). */
export const rhaiLanguage = new LanguageSupport(rhaiLRLanguage, [
  knownFunctionMarks(MSTREAM, "cm-rhai-mstream"),
  EditorView.theme({ ".cm-rhai-mstream": { fontWeight: "bold" } }),
]);

// The status bar's Format action: reindent every line of the script to its
// brace depth (the language's indent() above). Whitespace-only — there is no
// production Rhai formatter to do more, so this deliberately never rewraps
// or rewrites code.
export const reindentScript: StateCommand = ({ state, dispatch }) => {
  const changes = indentRange(state, 0, state.doc.length);
  if (changes.empty) return false;
  dispatch(state.update({ changes, userEvent: "format" }));
  return true;
};
